import React from 'react';
import styled from 'styled-components';
import { Typography } from '../../../index';
import { theme } from '../../theme/semanticTokens';

export type BadgeVariant =
  | 'infoPrimary'
  | 'infoSecondary'
  | 'criticalPrimary'
  | 'successPrimary'
  | 'warningPrimary'
  | 'warningSecondary'
  | 'fillSecondary'
  | 'clickable'
  | 'neutral';

export type BadgeSize = 'small' | 'large' | 'xs';

export type BadgeProps = {
  variant: BadgeVariant;
  size: BadgeSize;
  count?: number | string;
};

const StyledBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  &.xs {
    width: ${theme.dime.spacing.m};
    min-width: ${theme.dime.spacing.m};
    max-width: ${theme.dime.spacing.m};
    height: ${theme.dime.spacing.m};
    max-height: ${theme.dime.spacing.m};
    border-radius: ${theme.dime.radius.full};
    padding: 0;
  }

  &.small {
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    padding: 0 6px;
  }

  &.large {
    min-width: 24px;
    height: 24px;
    border-radius: 12px;
    padding: 0 6px;
  }

  &.infoPrimary {
    background-color: ${theme.dime.colors.fill.infoPrimary};
    color: ${theme.dime.colors.text.inverse};
  }

  &.infoSecondary {
    background-color: ${theme.dime.colors.fill.infoSecondary};
    color: ${theme.dime.colors.text.info};
  }

  &.warningPrimary {
    background-color: ${theme.dime.colors.fill.warningPrimary};
    color: ${theme.dime.colors.text.primary};
  }

  &.warningSecondary {
    background-color: ${theme.dime.colors.fill.warningSecondary};
    color: ${theme.dime.colors.text.primary};
  }

  &.criticalPrimary {
    background-color: ${theme.dime.colors.fill.criticalPrimary};
    color: ${theme.dime.colors.text.inverse};
  }

  &.successPrimary {
    background-color: ${theme.dime.colors.fill.successPrimary};
    color: ${theme.dime.colors.text.inverse};
  }

  &.fillSecondary {
    background-color: ${theme.dime.colors.fill.secondary};
    color: ${theme.dime.colors.text.primary};
    border: 1px solid ${theme.dime.colors.border.static};
  }

  &.clickable {
    background-color: ${theme.dime.colors.fill.infoPrimary};
    color: ${theme.dime.colors.text.inverse};
  }

  &.neutral {
    background-color: ${theme.dime.colors.fill.decorative10};
    color: ${theme.dime.colors.text.inverse};
  }
`;

export const Badge: React.FC<BadgeProps> = ({
  variant = 'infoPrimary',
  size = 'small',
  count = 0
}) => {
  return (
    <StyledBadge className={`${variant} ${size}`}>
      {size === 'xs' ? null : (
        <Typography variant="body2" color="inherit">
          {count}
        </Typography>
      )}
    </StyledBadge>
  );
};
